import React, { useCallback, useEffect, useMemo, useRef, useState, forwardRef, useImperativeHandle } from "react";
import { ScrollView, View, StyleSheet } from "react-native";
import { ThemedText } from "@/components/ThemedText";
import { Button } from "@/components/ui/Button";
import { useTheme } from "@/hooks/useTheme";
import { ClientType } from "@/types/db/clients";
import { clientService } from "@/services/clientService";
import Modal from "@/components/Modal";
import { ModalCard } from "@/components/Modals/ModalCard";
import { LocationPicker } from "@/components/Pickers/LocationPicker";
import { DressCodePicker } from "@/components/Pickers/DressCodePicker";

import { supabase } from "@/lib/supabase-client";
import useAuth from "@/hooks/useAuth";
import useToast from "@/hooks/useToast";
import { ProjectForm, ShiftForm, Position, ProjectPayload, MealOption } from "./types";
import { ProjectFields } from "./ProjectFields";
import { ShiftCard } from "./ShiftCard";
import type { LookupInputOption } from "@/components/ui/LookupInput";
import { TreeNode } from "@/components/ui/TreeNode";
import { theme } from "@/constants/themes";

export { ProjectPayload } from "./types";
export type { ProjectForm, ShiftForm, Position } from "./types";

// Status options for dropdown
const STATUS_OPTIONS = [
  { text: "POTENTIAL", color: "todo" },
  { text: "PENDING APPROVAL", color: "pending" },
  { text: "IN PROGRESS", color: "pending" },
  { text: "ON HOLD", color: "attention" },
  { text: "COMPLETED", color: "complete" },
  { text: "CONFLICTING", color: "attention" },
  { text: "LOST", color: "neutral" },
  { text: "CANCELED", color: "attention" },
] as const;

export function getProjectStatusColor(status: string, colors: theme): string {
  const mapping: Record<string, string> = {
    POTENTIAL: colors.todo.border,
    "PENDING APPROVAL": colors.pending.border,
    "IN PROGRESS": colors.pending.border,
    "ON HOLD": colors.attention.border,
    COMPLETED: colors.complete.border,
    CONFLICTING: colors.attention.border,
    LOST: colors.neutral.border,
    CANCELED: colors.attention.border,
  };
  return mapping[status.toUpperCase()] || colors.neutral.border;
}

export const PROJECT_STATUS_OPTIONS = STATUS_OPTIONS;

// Billing status options for dropdown
const BILLING_STATUS_OPTIONS = [
  { text: "PENDING", color: "neutral" },
  { text: "READY FOR BILLING", color: "todo" },
  { text: "BILLING IN PROGRESS", color: "pending" },
  { text: "PNL PENDING", color: "complete" },
] as const;

export function getProjectBillingStatusColor(status: string, colors: theme): string {
  const mapping: Record<string, string> = {
    PENDING: colors.neutral.border,
    "READY FOR BILLING": colors.todo.border,
    "BILLING IN PROGRESS": colors.pending.border,
    "PNL PENDING": colors.complete.border,
  };
  return mapping[status.toUpperCase()] || colors.neutral.border;
}

export const PROJECT_BILLING_STATUS_OPTIONS = BILLING_STATUS_OPTIONS;

export interface ProjectCreateFormProps {
  initialProject?: Partial<ProjectForm>;
  initialShifts?: ShiftForm[];
  onSave?: (payload: { project: ProjectForm; shifts: ShiftForm[] }) => Promise<void> | void;
  onChange?: (payload: ProjectPayload) => void;
  onDirtyChange?: (dirty: boolean) => void;
  onClientChange?: (client: ClientType | null) => void;
}

export interface ProjectCreateFormRef {
  handleSave: () => Promise<void>;
  handleReset: () => void;
  updateProjectName: (name: string) => void;
  updateProjectStatus: (status: string) => void;
  updateBillingStatus: (status: string) => void;
  openClientModal: () => void;
}

export const ProjectCreateForm = forwardRef<ProjectCreateFormRef, ProjectCreateFormProps>(
  ({ initialProject, initialShifts = [], onSave, onChange, onDirtyChange, onClientChange }, ref) => {
    const { colors } = useTheme();
    const { tenantId, displayNames } = useAuth();
    const toast = useToast();
    const [selectedClient, setSelectedClient] = useState<ClientType | null>(null);
    // Date validation errors per shift
    const [shiftDateErrors, setShiftDateErrors] = useState<{
      [key: number]: { endTimeError?: string };
    }>({});
    const [projectLocationModalVisible, setProjectLocationModalVisible] = useState(false);
    const [clientLocations, setClientLocations] = useState<any[]>([]);
    const [selectedLocation, setSelectedLocation] = useState<{
      id: string;
      name: string;
      address?: { street?: string; city?: string; state?: string; zip?: string };
    } | null>(null);
    const [dressCodeModalVisible, setDressCodeModalVisible] = useState(false);
    const [selectedDressCode, setSelectedDressCode] = useState<{
      id: string;
      name: string;
      description?: string;
    } | null>(null);
    const [locationMarket, setLocationMarket] = useState<{ id: string; name: string; fips_code?: string } | null>(null);

    // ---------- form state ----------
    const [project, setProject] = useState<ProjectForm>({
      name: initialProject?.name ?? "",
      client_id: initialProject?.client_id ?? "",
      status: initialProject?.status ?? "POTENTIAL",
      meta_data: {
        ...initialProject?.meta_data,
        requested_by: initialProject?.meta_data?.requested_by ?? "",
        markets: initialProject?.meta_data?.markets ?? [],
        abilities: initialProject?.meta_data?.abilities ?? [],
        restrictions: initialProject?.meta_data?.restrictions ?? [],
        billing_status: initialProject?.meta_data?.billing_status ?? "PENDING",
      },
    });

    const [shifts, setShifts] = useState<ShiftForm[]>(initialShifts || []);

    useEffect(() => {
      onChange?.({ project, shifts });
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [project, shifts]);

    // Load client if client_id is set initially
    useEffect(() => {
      const loadClient = async () => {
        if (initialProject?.client_id) {
          try {
            const client = await clientService.getClientById(initialProject.client_id);
            setSelectedClient(client);
            onClientChange?.(client);
            // Load client locations if available
            if (client.meta_data?.locations) {
              setClientLocations(client.meta_data.locations);
            }
          } catch (error) {
            console.error("Failed to load client:", error);
          }
        }
      };
      loadClient();
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [initialProject?.client_id]);

    // Load location if location_id is set initially
    useEffect(() => {
      const loadLocation = async () => {
        if (initialProject?.location_id) {
          try {
            const { data: location, error } = await supabase
              .schema("lists")
              .from("locations")
              .select("id, name, street, city, state, zip")
              .eq("id", initialProject.location_id)
              .single();

            if (!error && location) {
              setSelectedLocation({
                id: location.id,
                name: location.name,
                address: {
                  street: location.street,
                  city: location.city,
                  state: location.state,
                  zip: location.zip,
                },
              });
            }
          } catch (error) {
            console.error("Failed to load location:", error);
          }
        }
      };
      loadLocation();
    }, [initialProject?.location_id]);

    // Load location's market (inherited from location)
    useEffect(() => {
      const loadLocationMarket = async () => {
        if (!selectedLocation?.id) {
          setLocationMarket(null);
          return;
        }

        try {
          // First get the location to find its market_id
          const { data: location, error: locationError } = await supabase
            .schema("lists")
            .from("locations")
            .select("market_id")
            .eq("id", selectedLocation.id)
            .single();

          if (locationError || !location?.market_id) {
            setLocationMarket(null);
            return;
          }

          // Then fetch the market details
          const { data: market, error: marketError } = await supabase
            .schema("lists")
            .from("markets")
            .select("id, name, fips_code")
            .eq("id", location.market_id)
            .single();

          if (!marketError && market) {
            setLocationMarket(market);
          } else {
            setLocationMarket(null);
          }
        } catch (err) {
          console.error("Error loading location market:", err);
          setLocationMarket(null);
        }
      };

      loadLocationMarket();
    }, [selectedLocation?.id]);

    const baseline = useRef(JSON.stringify({ project, shifts }));
    const dirty = useMemo(() => JSON.stringify({ project, shifts }) !== baseline.current, [project, shifts]);

    // Validate shift dates
    useEffect(() => {
      const errors: { [key: number]: { endTimeError?: string } } = {};

      shifts.forEach((shift, index) => {
        // Check if end time is after start time
        if (shift.start_time && shift.end_time) {
          const startDate = new Date(shift.start_time);
          const endDate = new Date(shift.end_time);
          if (endDate <= startDate) {
            errors[index] = { endTimeError: "End time must be after start time" };
          }
        }
      });

      setShiftDateErrors(errors);
    }, [shifts]);

    // Notify parent of dirty state changes
    useEffect(() => {
      onDirtyChange?.(dirty);
    }, [dirty, onDirtyChange]);

    // Expose handleSave, handleReset, updateProjectName, updateProjectStatus, and openClientModal to parent via ref
    useImperativeHandle(ref, () => ({
      handleSave,
      handleReset,
      updateProjectName: (name: string) => {
        setProject((p) => ({ ...p, name }));
      },
      updateProjectStatus: (status: string) => {
        setProject((p) => ({ ...p, status }));
      },
      updateBillingStatus: (status: string) => {
        setProject((p) => ({
          ...p,
          meta_data: { ...p.meta_data, billing_status: status },
        }));
      },
      openClientModal: () => {
        // Client selection is now inline via LookupInput
      },
    }));

    // ---------- contact options (fetched once per client) ----------
    const [contactOptions, setContactOptions] = useState<LookupInputOption[]>([]);

    useEffect(() => {
      if (!project.client_id) {
        setContactOptions([]);
        return;
      }
      let cancelled = false;
      const loadContacts = async () => {
        try {
          // Get contact IDs linked to this client via the link table
          const { data: links, error: linkError } = await supabase
            .schema("link")
            .from("clients_contacts")
            .select("contact_id")
            .eq("id", project.client_id);
          if (linkError) throw linkError;
          if (!links || links.length === 0) {
            if (!cancelled) setContactOptions([]);
            return;
          }
          const contactIds = links.map((l: any) => l.contact_id);
          const { data: contacts, error: contactError } = await supabase
            .from("contacts")
            .select("id, first_name, last_name, email_address, phone_number")
            .in("id", contactIds)
            .order("last_name", { ascending: true });
          if (contactError) throw contactError;
          if (cancelled) return;
          setContactOptions(
            (contacts ?? []).map((c: any) => ({
              id: c.id ?? "",
              name: `${c.first_name} ${c.last_name ?? ""}`.trim(),
              subtitle: c.email_address ?? c.phone_number ?? undefined,
              avatar: {
                firstName: c.first_name ?? undefined,
                lastName: c.last_name ?? undefined,
                email: c.email_address ?? undefined,
              },
            })),
          );
        } catch (err) {
          console.error("Failed to load contacts:", err);
          if (!cancelled) setContactOptions([]);
        }
      };
      loadContacts();
      return () => {
        cancelled = true;
      };
    }, [project.client_id]);

    // ---------- location options (fetched once on mount) ----------
    const [locationOptions, setLocationOptions] = useState<LookupInputOption[]>([]);

    useEffect(() => {
      let cancelled = false;
      const loadLocations = async () => {
        try {
          const { data, error } = await supabase
            .schema("lists")
            .from("locations")
            .select("id, name, street, city, state, zip")
            .order("name", { ascending: true });
          if (error) throw error;
          if (cancelled) return;
          setLocationOptions(
            (data ?? []).map((loc: any) => ({
              id: loc.id,
              name: loc.name,
              subtitle: [loc.street, loc.city, loc.state, loc.zip].filter(Boolean).join(", ") || undefined,
              icon: "map-marker" as const,
            })),
          );
        } catch (err) {
          console.error("Failed to load locations:", err);
        }
      };
      loadLocations();
      return () => {
        cancelled = true;
      };
    }, []);

    // ---------- dress code options (fetched once on mount) ----------
    const [dressCodeOptions, setDressCodeOptions] = useState<LookupInputOption[]>([]);

    useEffect(() => {
      let cancelled = false;
      const loadDressCodes = async () => {
        try {
          const { data, error } = await supabase
            .schema("lists")
            .from("dress_codes")
            .select("id, name, description")
            .order("name", { ascending: true });
          if (error) throw error;
          if (cancelled) return;
          setDressCodeOptions(
            (data ?? []).map((dc: any) => ({
              id: dc.id,
              name: dc.name,
              subtitle: dc.description || undefined,
              icon: "tshirt-crew" as const,
            })),
          );
        } catch (err) {
          console.error("Failed to load dress codes:", err);
        }
      };
      loadDressCodes();
      return () => {
        cancelled = true;
      };
    }, []);

    // ---------- helpers ----------

    const handleClientSelect = useCallback(
      async (clientId: string | null) => {
        if (!clientId) {
          setSelectedClient(null);
          onClientChange?.(null);
          setClientLocations([]);
          setProject((p) => ({ ...p, client_id: "", client_name: "" }));
          return;
        }
        try {
          const client = await clientService.getClientById(clientId);
          setSelectedClient(client);
          onClientChange?.(client);
          const clientLocs = client.meta_data?.locations || [];
          setClientLocations(clientLocs);
          setProject((p) => ({
            ...p,
            client_id: client.id ?? "",
            client_name: client.name,
          }));
        } catch (error) {
          console.error("Failed to load client:", error);
          toast.showToast({ title: "Error", message: "Failed to load client details", toastType: "error" });
        }
      },
      [onClientChange, toast],
    );

    const handleContactSelect = useCallback(
      (field: "requested_by" | "primary_contact" | "onsite_contact", contactId: string | null) => {
        updateProject({ [field]: contactId ?? undefined });
      },
      [],
    );

    const handleLocationSelect = useCallback(async (locationId: string | null) => {
      if (!locationId) {
        setSelectedLocation(null);
        updateProject({ location_id: "" });
        return;
      }
      try {
        const { data: location, error } = await supabase
          .schema("lists")
          .from("locations")
          .select("*")
          .eq("id", locationId)
          .single();
        if (error) throw error;
        setSelectedLocation({
          id: location.id,
          name: location.name,
          address: {
            street: location.street,
            city: location.city,
            state: location.state,
            zip: location.zip,
          },
        });
        updateProject({ location_id: location.id });
      } catch (err) {
        console.error("Failed to load location:", err);
        toast.showToast({ title: "Error", message: "Failed to load location details", toastType: "error" });
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleDressCodeSelect = useCallback(
      (dressCodeId: string | null) => {
        if (!dressCodeId) {
          setSelectedDressCode(null);
          updateProject({ meta_data: { ...project.meta_data, dress_code_id: undefined } });
          return;
        }
        const opt = dressCodeOptions.find((o) => o.id === dressCodeId);
        setSelectedDressCode({
          id: dressCodeId,
          name: opt?.name ?? "",
          description: opt?.subtitle,
        });
        updateProject({ meta_data: { ...project.meta_data, dress_code_id: dressCodeId } });
      },
      [dressCodeOptions, project.meta_data],
    );

    const updateProject = (patch: Partial<ProjectForm>) => setProject((p) => ({ ...p, ...patch }));

    const updateShift = (i: number, patch: Partial<ShiftForm>) =>
      setShifts((arr) => arr.map((s, idx) => (idx === i ? { ...s, ...patch } : s)));

    const addShift = () => {
      const empty: ShiftForm = {
        name: "",
        description: "",
        start_time: "",
        end_time: "",
        meta_data: {
          location: null,
        },
        positions: [],
      };
      if (!shifts) return setShifts([empty]);
      return setShifts((arr) => [...arr, empty]);
    };

    const duplicateShift = (shiftIndex: number) => {
      const shiftToDuplicate = shifts[shiftIndex];
      const duplicatedShift: ShiftForm = {
        ...shiftToDuplicate,
        name: shiftToDuplicate.name ? `${shiftToDuplicate.name} (copy)` : null,
        positions: shiftToDuplicate.positions.map((p) => ({ ...p, position_id: p.position_id })),
        meta_data: shiftToDuplicate.meta_data
          ? {
              ...shiftToDuplicate.meta_data,
              meal_option: shiftToDuplicate.meta_data.meal_option?.map((m) => ({ ...m })),
            }
          : undefined,
      };
      const newShifts = [...shifts];
      newShifts.splice(shiftIndex + 1, 0, duplicatedShift);
      setShifts(newShifts);
    };

    const addPosition = (shiftIndex: number) =>
      setShifts((arr) =>
        arr.map((shift, idx) => {
          if (idx !== shiftIndex) return shift;
          const empty = {
            name: "",
            description: "",
            gifted_hours: 4,
            supervisory: false,
          };
          return {
            ...shift,
            positions: [...shift.positions, empty],
          };
        }),
      );

    const removePosition = (shiftIndex: number, posIndex: number) => {
      setShifts((arr) =>
        arr.map((shift, idx) =>
          idx === shiftIndex
            ? {
                ...shift,
                positions: shift.positions.filter((_, i) => i !== posIndex),
              }
            : shift,
        ),
      );
    };

    const duplicatePosition = (shiftIndex: number, posIndex: number) => {
      setShifts((arr) =>
        arr.map((shift, idx) => {
          if (idx !== shiftIndex) return shift;
          const positionToDuplicate = shift.positions[posIndex];
          const duplicatedPosition = {
            ...positionToDuplicate,
            position_id: positionToDuplicate.position_id,
            location_override: positionToDuplicate.location_override
              ? { ...positionToDuplicate.location_override }
              : undefined,
          };
          const newPositions = [...shift.positions];
          newPositions.splice(posIndex + 1, 0, duplicatedPosition);
          return { ...shift, positions: newPositions };
        }),
      );
    };

    const removeShift = (i: number) => {
      setShifts((arr) => arr.filter((_, idx) => idx !== i));
    };

    const updatePosition = (shiftIndex: number, posIndex: number, patch: Position) =>
      setShifts((arr) =>
        arr.map((shift, si) =>
          si === shiftIndex
            ? {
                ...shift,
                positions: shift.positions.map((p, pi) => (pi === posIndex ? { ...p, ...patch } : p)),
              }
            : shift,
        ),
      );

    // Meal option management functions (array-based)
    const addMealOption = (shiftIndex: number) => {
      const currentOptions = shifts[shiftIndex].meta_data?.meal_option || [];
      updateShift(shiftIndex, {
        meta_data: {
          ...shifts[shiftIndex].meta_data,
          meal_option: [...currentOptions, { type: "UNPAID" as const, duration: 30, after: 0 }],
        },
      });
    };

    const removeMealOption = (shiftIndex: number, mealIndex: number) => {
      const currentOptions = shifts[shiftIndex].meta_data?.meal_option || [];
      const updated = currentOptions.filter((_, i) => i !== mealIndex);
      updateShift(shiftIndex, {
        meta_data: {
          ...shifts[shiftIndex].meta_data,
          meal_option: updated.length > 0 ? updated : undefined,
        },
      });
    };

    const updateMealOption = (shiftIndex: number, mealIndex: number, patch: Partial<MealOption>) => {
      const currentOptions = shifts[shiftIndex].meta_data?.meal_option || [];
      const updated = currentOptions.map((opt, i) => (i === mealIndex ? { ...opt, ...patch } : opt));
      updateShift(shiftIndex, {
        meta_data: {
          ...shifts[shiftIndex].meta_data,
          meal_option: updated,
        },
      });
    };

    const handleSave = async () => {
      // quick client validations
      if (!project.client_id?.trim()) {
        toast.showToast({
          title: "Missing Client",
          message: "Please select a client for this project",
          toastType: "warning",
        });
        return;
      }
      if (!project.name?.trim()) {
        toast.showToast({
          title: "Missing Project Name",
          message: "Please enter a name for this project",
          toastType: "warning",
        });
        return;
      }
      if (!project.status) {
        toast.showToast({
          title: "Missing Status",
          message: "Please select a status for this project",
          toastType: "warning",
        });
        return;
      }

      // Check for date validation errors
      if (Object.keys(shiftDateErrors).length > 0) {
        const firstErrorIndex = parseInt(Object.keys(shiftDateErrors)[0]);
        const firstError = shiftDateErrors[firstErrorIndex];
        toast.showToast({
          title: "Invalid Shift Times",
          message: `Shift ${firstErrorIndex + 1}: ${firstError.endTimeError}`,
          toastType: "warning",
        });
        return;
      }

      // Validate shift times and positions
      for (let i = 0; i < shifts.length; i++) {
        const shift = shifts[i];

        // Require start and end times
        if (!shift.start_time) {
          toast.showToast({
            title: "Missing Start Time",
            message: `Shift ${i + 1}: Please select a start time`,
            toastType: "warning",
          });
          return;
        }
        if (!shift.end_time) {
          toast.showToast({
            title: "Missing End Time",
            message: `Shift ${i + 1}: Please select an end time`,
            toastType: "warning",
          });
          return;
        }

        // Validate start time is before end time
        const startTime = new Date(shift.start_time);
        const endTime = new Date(shift.end_time);
        if (startTime >= endTime) {
          toast.showToast({
            title: "Invalid Shift Times",
            message: `Shift ${i + 1}: Start time must be before end time`,
            toastType: "warning",
          });
          return;
        }

        // Validate positions have position_id (must be selected from picker)
        for (let j = 0; j < shift.positions.length; j++) {
          const position = shift.positions[j];
          if (!position.position_id) {
            toast.showToast({
              title: "Position Not Selected",
              message: `Shift ${i + 1}, Position ${j + 1}: Please select a position using the "Select" button`,
              toastType: "warning",
            });
            return;
          }
        }
      }

      try {
        await onSave?.({
          project,
          shifts:
            shifts?.map((s) => {
              // Resolve position locations (inherit from shift or use override)
              const resolvedPositions = s.positions?.map((pos) => {
                let finalLocation;

                if (!pos.location_override || !pos.location_override.location) {
                  // No location override defined or no location specified, inherit from shift
                  finalLocation = s.meta_data?.location;
                } else {
                  // Use custom location from override
                  finalLocation = pos.location_override.location;
                }

                return {
                  ...pos,
                  position_id: pos.position_id, // Ensure position_id is passed through
                  // Remove location_override from final payload, replace with resolved location
                  location_override: undefined,
                  location: finalLocation,
                };
              });

              return {
                ...s,
                start_time: s.start_time ? s.start_time : null,
                end_time: s.end_time ? s.end_time : null,
                positions: resolvedPositions,
              };
            }) || [],
        });
        baseline.current = JSON.stringify({ project, shifts });
      } catch (e: any) {
        throw e; // Re-throw to let parent handle it
      }
    };

    const handleReset = () => {
      const nextProject: ProjectForm = {
        name: initialProject?.name ?? "",
        client_id: initialProject?.client_id ?? "",
        status: initialProject?.status ?? "POTENTIAL",
        meta_data: {
          ...initialProject?.meta_data,
          abilities: initialProject?.meta_data?.abilities ?? [],
          restrictions: initialProject?.meta_data?.restrictions ?? [],
          billing_status: initialProject?.meta_data?.billing_status ?? "PENDING",
        },
      };
      const nextShifts: ShiftForm[] = initialShifts || [];
      setProject(nextProject);
      setShifts(nextShifts);
      setSelectedClient(null);
      setSelectedLocation(null);
      setSelectedDressCode(null);
      setClientLocations([]);
      onClientChange?.(null);
      baseline.current = JSON.stringify({ project: nextProject, shifts: nextShifts });
    };

    return (
      <View style={styles.container}>
        <ScrollView contentContainerStyle={{ gap: 8 }}>
          <ProjectFields
            project={project}
            locationMarket={locationMarket}
            updateProject={updateProject}
            onClientSelect={handleClientSelect}
            contactOptions={contactOptions}
            onContactSelect={handleContactSelect}
            locationOptions={locationOptions}
            onLocationSelect={handleLocationSelect}
            dressCodeOptions={dressCodeOptions}
            onDressCodeSelect={handleDressCodeSelect}
            marketIds={project.meta_data?.markets || []}
            onMarketsChange={(ids) => {
              updateProject({ meta_data: { ...project.meta_data, markets: ids } });
            }}
          />

          {/* Shifts */}
          <ThemedText style={{ minWidth: 100 }} type="subtitle">
            {displayNames?.shifts?.plural || "Shifts"} {`${shifts.length && " (" + shifts.length + ")"}`}
          </ThemedText>

          {shifts?.length
            ? shifts.map((s, i) => {
                return (
                  <TreeNode key={i} depth={0} gap={8} isLast={false}>
                    <ShiftCard
                      shift={s}
                      index={i}
                      updateShift={updateShift}
                      removeShift={removeShift}
                      duplicateShift={duplicateShift}
                      addPosition={addPosition}
                      removePosition={removePosition}
                      duplicatePosition={duplicatePosition}
                      updatePosition={updatePosition}
                      addMealOption={addMealOption}
                      removeMealOption={removeMealOption}
                      updateMealOption={updateMealOption}
                      projectLocation={selectedLocation}
                      locationOptions={locationOptions}
                      onLocationChange={async (shiftIndex, locationId) => {
                        if (!locationId) {
                          updateShift(shiftIndex, {
                            meta_data: { ...shifts[shiftIndex].meta_data, location: null },
                          });
                          return;
                        }
                        try {
                          const { data: location, error } = await supabase
                            .schema("lists")
                            .from("locations")
                            .select("id, name, street, city, state, zip")
                            .eq("id", locationId)
                            .single();
                          if (error) throw error;
                          updateShift(shiftIndex, {
                            meta_data: {
                              ...shifts[shiftIndex].meta_data,
                              location: {
                                id: location.id,
                                name: location.name,
                                address: {
                                  street: location.street,
                                  city: location.city,
                                  state: location.state,
                                  zip: location.zip,
                                },
                              },
                            },
                          });
                        } catch (err) {
                          console.error("Failed to load location:", err);
                        }
                      }}
                      onPositionLocationChange={async (shiftIndex, positionIndex, locationId) => {
                        if (!locationId) {
                          updatePosition(shiftIndex, positionIndex, { location_override: undefined });
                          return;
                        }
                        try {
                          const { data: location, error } = await supabase
                            .schema("lists")
                            .from("locations")
                            .select("id, name, street, city, state, zip")
                            .eq("id", locationId)
                            .single();
                          if (error) throw error;
                          updatePosition(shiftIndex, positionIndex, {
                            location_override: {
                              inherit: false,
                              location: {
                                id: location.id,
                                name: location.name,
                                address: {
                                  street: location.street,
                                  city: location.city,
                                  state: location.state,
                                  zip: location.zip,
                                },
                              },
                            },
                          });
                        } catch (err) {
                          console.error("Failed to load location:", err);
                        }
                      }}
                    />
                  </TreeNode>
                );
              })
            : null}

          <TreeNode gap={12} depth={0} isLast isAddButton>
            <Button
              id="add-shift-button"
              icon="plus"
              text="Add another shift"
              iconPosition="right"
              iconColor="white"
              style={{
                height: 35,
                backgroundColor: colors.primary,
                paddingHorizontal: 16,
              }}
              textStyle={{ color: "white" }}
              onPress={addShift}
            />
          </TreeNode>
        </ScrollView>

        {/* Project Location Picker Modal */}
        {projectLocationModalVisible && (
          <Modal onUnmount={() => setProjectLocationModalVisible(false)}>
            <ModalCard>
              <LocationPicker
                onPick={async (locationId) => {
                  // Fetch the full location details from Supabase
                  const { data: location, error } = await supabase
                    .schema("lists")
                    .from("locations")
                    .select("*")
                    .eq("id", locationId)
                    .single();

                  if (error) {
                    console.error("Failed to fetch location details:", error);
                    toast.showToast({
                      title: "Error",
                      message: "Failed to load location details",
                      toastType: "error",
                    });
                    return;
                  }

                  setSelectedLocation({
                    id: location.id,
                    name: location.name,
                    address: {
                      street: location.street,
                      city: location.city,
                      state: location.state,
                      zip: location.zip,
                    },
                  });
                  updateProject({
                    location_id: location.id,
                  });
                  setProjectLocationModalVisible(false);
                }}
                onCancel={() => {
                  setProjectLocationModalVisible(false);
                }}
              />
            </ModalCard>
          </Modal>
        )}

        {/* Dress Code Picker Modal */}
        {dressCodeModalVisible && (
          <Modal onUnmount={() => setDressCodeModalVisible(false)}>
            <ModalCard>
              <DressCodePicker
                onPick={async (dressCodeId) => {
                  const { data: dressCode, error } = await supabase
                    .schema("lists")
                    .from("dress_codes")
                    .select("id, name, description")
                    .eq("id", dressCodeId)
                    .single();

                  if (error) {
                    console.error("Failed to fetch dress code details:", error);
                    toast.showToast({
                      title: "Error",
                      message: "Failed to load dress code details",
                      toastType: "error",
                    });
                    return;
                  }

                  setSelectedDressCode({
                    id: dressCode.id,
                    name: dressCode.name,
                    description: dressCode.description || undefined,
                  });
                  updateProject({
                    meta_data: {
                      ...project.meta_data,
                      dress_code_id: dressCode.id,
                    },
                  });
                  setDressCodeModalVisible(false);
                }}
                onCancel={() => setDressCodeModalVisible(false)}
              />
            </ModalCard>
          </Modal>
        )}
      </View>
    );
  },
);

ProjectCreateForm.displayName = "ProjectCreateForm";

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
});
